"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleX as XCircle, TrendingUp, CircleAlert as AlertCircle, ArrowRight, CalendarClock, ShieldCheck, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  id: 1,
  topic: "Search algorithms & SERP changes",
  oneOffTitle: "Static audit report",
  oneOffDesc: "A snapshot of issues on day one that quickly becomes outdated as Google releases core updates and search layouts shift.",
  ongoingTitle: "Continuous adaptation",
  ongoingDesc: "I monitor search changes weekly, adjusting your strategy in real time so algorithm updates help your rankings rather than hurt them."
}, {
  id: 2,
  topic: "Competitor movements",
  oneOffTitle: "One-time comparison",
  oneOffDesc: "Analyzes what rivals did last month, leaving you blind when they publish new pages or acquire fresh backlinks next quarter.",
  ongoingTitle: "Proactive counter-moves",
  ongoingDesc: "I track your top competitors monthly to spot their new content clusters, backlink wins, and keyword gaps before they outrank you."
}, {
  id: 3,
  topic: "Implementation & fixing",
  oneOffTitle: "Handover PDF backlog",
  oneOffDesc: "Leaves your internal developers or marketers with a 50-page document of recommendations that often sits unbuilt for months.",
  ongoingTitle: "Direct hands-on execution",
  ongoingDesc: "Structured monthly sprints turn audit findings into deployed fixes, guiding your developers with precise tickets or shipping on-page updates directly."
}, {
  id: 4,
  topic: "Content & commercial intent",
  oneOffTitle: "Single batch of copy",
  oneOffDesc: "A set of initial target pages that slowly loses topical relevance as user intent and search phrasing evolve.",
  ongoingTitle: "Compound topical authority",
  ongoingDesc: "We continuously build targeted commercial and informational hubs, compounding your organic visibility across high-intent search terms."
}, {
  id: 5,
  topic: "Measurement & accountability",
  oneOffTitle: "No outcome ownership",
  oneOffDesc: "The project ends when the document delivers, with zero follow-up on whether traffic or enquiries actually increased.",
  ongoingTitle: "Honest commercial tracking",
  ongoingDesc: "I track bottom-line revenue, qualified enquiries, and qualified rankings every month with direct, jargon-free video summaries."
}];
export default function OngoingSeo() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="ongoingvsoneoffseo" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* Subtle decorative background accent */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-secondary/40 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 -left-20 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
        
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold px-3 py-1 bg-secondary text-secondary-foreground border border-border">
            Strategic Comparison
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
            Why ongoing SEO outperforms one-off projects
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            In my experience working directly with growing businesses, one-off audits pinpoint problems, but steady, month-by-month optimisation creates durable market share and consistent inbound revenue.
          </p>
        </div>

        {/* Quick Snapshot Highlight Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 md:mb-16">
          <div className="bg-card border border-border rounded-xl p-6 shadow-xs flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0">
              <TrendingUp className="size-5" />
            </div>
            <div>
              <h3 className="font-default font-semibold text-foreground text-base mb-1">
                Compound growth
              </h3>
              <p className="text-sm text-muted-foreground leading-normal">
                Organic search builds momentum. Each month builds upon previous technical and content authority.
              </p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 shadow-xs flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0">
              <CalendarClock className="size-5" />
            </div>
            <div>
              <h3 className="font-default font-semibold text-foreground text-base mb-1">
                Algorithm stability
              </h3>
              <p className="text-sm text-muted-foreground leading-normal">
                Continuous testing insulates your rankings against Google core rollouts and AI search shifts.
              </p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 shadow-xs flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0">
              <ShieldCheck className="size-5" />
            </div>
            <div>
              <h3 className="font-default font-semibold text-foreground text-base mb-1">
                Direct accountability
              </h3>
              <p className="text-sm text-muted-foreground leading-normal">
                Structured monthly retainer cycles provide dedicated execution hours, rolling sprint roadmaps, and measurable deliverables every single month.
              </p>
            </div>
          </div>
        </div>

        {/* Side-by-Side Comparison Container */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="space-y-6">
          {/* Column Header Titles for Desktop */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-6 items-center px-4 pb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            <div className="col-span-3">Strategic Area</div>
            <div className="col-span-4 flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="size-4 text-muted-foreground" />
              One-off project approach
            </div>
            <div className="col-span-5 flex items-center gap-2 text-primary font-bold">
              <CheckCircle2 className="size-4 text-primary" />
              Ongoing consultancy with me
            </div>
          </div>

          {comparisonPoints.map((point, index) => <motion.div key={point.id} data-index={index} variants={itemVariants} className="bg-card border border-border rounded-xl p-6 lg:p-7 shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                
                {/* Topic / Factor */}
                <div className="lg:col-span-3 flex flex-col justify-start">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">
                    Point 0{index + 1}
                  </span>
                  <h4 className="font-default text-base md:text-lg font-semibold text-foreground">
                    {point.topic}
                  </h4>
                </div>

                {/* One-off side */}
                <div className="lg:col-span-4 bg-muted/40 rounded-lg p-4 border border-border/70">
                  <div className="flex items-center gap-2 mb-2 text-muted-foreground">
                    <XCircle className="size-4 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm text-foreground">
                      {point.oneOffTitle}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {point.oneOffDesc}
                  </p>
                </div>

                {/* Ongoing side */}
                <div className="lg:col-span-5 bg-secondary/30 border border-primary/20 rounded-lg p-4 shadow-2xs">
                  <div className="flex items-center gap-2 mb-2 text-primary">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="font-semibold text-sm text-foreground">
                      {point.ongoingTitle}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-card-foreground leading-relaxed">
                    {point.ongoingDesc}
                  </p>
                </div>

              </div>
            </motion.div>)}
        </motion.div>

        {/* Bottom Consultation Box */}
        <div className="mt-16 md:mt-20">
          <Card className="bg-card border-2 border-primary/20 shadow-md overflow-hidden relative">
            <div className="absolute top-0 right-0 -mt-8 -mr-8 w-48 h-48 bg-secondary/50 rounded-full blur-2xl pointer-events-none" />
            
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <Badge variant="outline" className="mb-2 text-primary border-primary/30 font-medium">
                    Sustainable Search Growth
                  </Badge>
                  <CardTitle className="text-2xl sm:text-3xl font-bold font-default text-foreground">
                    Need a dedicated partner rather than another audit file?
                  </CardTitle>
                </div>
              </div>
              <CardDescription className="text-base text-muted-foreground max-w-2xl mt-2">
                I take on a limited roster of ongoing client partnerships so every website gets my hands-on attention, continuous testing, and direct communication.
              </CardDescription>
            </CardHeader>

            <Separator className="my-2 bg-border" />

            <CardContent className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Search className="size-4 text-primary shrink-0" />
                <span>Flexible rolling monthly retainers with clear sprint goals and measurable execution every cycle.</span>
              </div>
              
              <div className="flex flex-wrap items-center gap-3 shrink-0">
                <Button variant="outline" asChild>
                  <Link to="/CaseStudiesPage">
                    Explore Case Studies
                  </Link>
                </Button>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90" asChild>
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </section>;
}